// src/components/tasks/TaskFormModal.tsx
//
// The single task modal — handles CREATE, VIEW and EDIT so creation and editing
// never diverge. Create stays fast: title autofocuses, advanced sections are
// collapsed, and a task can be created from the basic fields alone.
//
// Recurrence is integrated as a create-only "Repeat" section. When Repeat is ON
// the submit creates a TaskTemplate (recurring series); when OFF it creates a
// one-off task. Recurrence pattern UI/logic is shared via RecurrenceFields +
// services/recurrence (no duplication). Editing an existing one-off task does
// not offer conversion to recurring yet (deferred); generated recurring tasks
// keep the read-only recurrence display.

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  useGetTaskQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
} from '../../api/tasksApi';
import {
  useGetTaskTemplateQuery,
  useCreateTaskTemplateMutation,
} from '../../api/taskTemplatesApi';
import { useGetContactsQuery } from '../../api/contactsApi';
import { useGetManagementSummaryQuery, useGetReassignmentSuggestionsQuery } from '../../api/managementApi';
import { useGetGoalsQuery } from '../../api/goalsApi';
import { useGetProjectsQuery } from '../../api/projectsApi';
import { useGetTeamMembersQuery } from '../../api/teamApi';
import { useCreateConversationMutation } from '../../api/conversationsApi';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import LoadingSpinner from '../common/LoadingSpinner';
import TaskChecklist from './TaskChecklist';
import { AssigneeSelect, WatcherPicker } from './TaskMemberFields';
import RecurrenceFields from './RecurrenceFields';
import TaskAttachments from './TaskAttachments';
import { isCompletionBlockedByProof } from '../../utils/proofGate';
import { useWorkAccountabilityEnabled } from '../../hooks/useWorkAccountability';
import TaskAccountabilitySection from '../workAccountability/TaskAccountabilitySection';
import {
  formatRecurrence,
  buildRecurrenceRule,
  defaultRecurrencePattern,
  type RecurrencePatternState,
} from '../../services/recurrence';
import { RefreshCw, Calendar, ExternalLink, ChevronDown } from 'lucide-react';
import type { Task, TaskStatus, TaskPriority } from '../../types/api';

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

const STATUS_CONFIG: Record<TaskStatus, { label: string; color: string; bgColor: string }> = {
  todo: { label: 'To Do', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  in_progress: { label: 'In Progress', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  blocked: { label: 'Blocked', color: 'text-red-700', bgColor: 'bg-red-100' },
  done: { label: 'Done', color: 'text-green-700', bgColor: 'bg-green-100' },
};

const PRIORITY_CONFIG: Record<TaskPriority, { label: string; color: string }> = {
  low: { label: 'Low', color: 'text-gray-500' },
  medium: { label: 'Medium', color: 'text-yellow-600' },
  high: { label: 'High', color: 'text-orange-600' },
  urgent: { label: 'Urgent', color: 'text-red-600' },
};

const STATUSES: TaskStatus[] = ['todo', 'in_progress', 'blocked', 'done'];
const PRIORITIES: TaskPriority[] = ['low', 'medium', 'high', 'urgent'];

const INPUT_CLS =
  'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

function nowForDatetimeLocal(): string {
  return new Date().toISOString().slice(0, 16);
}

// ── Collapsible advanced section ──────────────────────────────
function CollapsibleSection({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-lg">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
      >
        {title}
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-4 pb-4 pt-1 space-y-4">{children}</div>}
    </div>
  );
}

interface TaskFormModalProps {
  open: boolean;
  /** null = create mode. */
  taskId: string | null;
  workspaceId: string;
  onClose: () => void;
  onCreated?: () => void;
  /** Work Accountability: request completion be handled by the parent (opens the
   *  completion dialog at page level, avoiding a modal-inside-a-modal). */
  onRequestComplete?: (taskId: string) => void;
}

export default function TaskFormModal({
  open,
  taskId,
  workspaceId,
  onClose,
  onCreated,
  onRequestComplete,
}: TaskFormModalProps) {
  const navigate = useNavigate();
  const isCreate = taskId === null;

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<Task>>({});
  const [followerIds, setFollowerIds] = useState<string[]>([]);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  // Live proof-attachment count (from TaskAttachments) for client-side gating.
  const [proofCount, setProofCount] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);

  // Create-only recurrence ("Repeat") state
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [recurrence, setRecurrence] = useState<RecurrencePatternState>({ ...defaultRecurrencePattern });
  const [seriesStartAt, setSeriesStartAt] = useState<string>(nowForDatetimeLocal());
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { data: task, isLoading, error } = useGetTaskQuery(taskId!, { skip: !taskId });

  const { data: template } = useGetTaskTemplateQuery(task?.taskTemplateId!, {
    skip: !task?.taskTemplateId,
  });

  const { data: contactsResponse } = useGetContactsQuery({ workspaceId }, { skip: !workspaceId });
  const contacts = contactsResponse?.contacts;

  const { data: teamMembers, isLoading: isLoadingMembers } = useGetTeamMembersQuery(
    { workspaceId },
    { skip: !workspaceId },
  );

  const [createTask, { isLoading: isCreating }] = useCreateTaskMutation();
  const [createTaskTemplate, { isLoading: isCreatingTemplate }] = useCreateTaskTemplateMutation();
  const [updateTask, { isLoading: isSaving }] = useUpdateTaskMutation();
  const [createConversation] = useCreateConversationMutation();
  const waEnabled = useWorkAccountabilityEnabled();

  const { data: managementSummary } = useGetManagementSummaryQuery(undefined, { skip: !workspaceId });
  const { data: reassignment } = useGetReassignmentSuggestionsQuery(taskId!, {
    skip: !taskId || isEditing,
  });
  const { data: goalsData } = useGetGoalsQuery({ workspaceId, status: 'active' }, { skip: !workspaceId });
  const { data: projects } = useGetProjectsQuery({ workspaceId }, { skip: !workspaceId });

  // Hydrate the form from an existing task.
  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title,
        description: task.description,
        status: task.status,
        priority: task.priority,
        dueDate: task.dueDate,
        assigneeUserId: task.assigneeUserId,
        relatedContactId: task.relatedContactId,
        estimatedMinutes: task.estimatedMinutes,
        blockerReason: task.blockerReason,
        reopenReason: task.reopenReason,
        completionScore: task.completionScore,
        goalId: task.goalId ?? null,
        projectId: task.projectId ?? null,
        milestoneId: task.milestoneId ?? null,
        proofRequired: task.proofRequired ?? false,
      });
      setFollowerIds((task.followers ?? []).map((f) => f.id));
      setProofCount((task.attachments ?? []).filter((a) => a.isProof).length);
    }
  }, [task]);

  // Reset to a clean form each time the create modal opens.
  useEffect(() => {
    if (isCreate && open) {
      setFormData({ status: 'todo', priority: 'medium', dueDate: null, assigneeUserId: null, projectId: null, milestoneId: null, proofRequired: false });
      setFollowerIds([]);
      setRepeatEnabled(false);
      setRecurrence({ ...defaultRecurrencePattern });
      setSeriesStartAt(nowForDatetimeLocal());
      setProofCount(0);
      setActionError(null);
      setIsEditing(true);
    }
    if (isCreate && !open) {
      setIsEditing(false);
    }
  }, [isCreate, open]);

  const showForm = isCreate || isEditing;

  // Client-side proof gate: block completion when proof is required but absent.
  // (The server enforces this authoritatively; this gives an immediate message.)
  const proofBlocksCompletion = isCompletionBlockedByProof({
    prevStatus: task?.status,
    nextStatus: formData.status,
    proofRequired: formData.proofRequired,
    proofCount,
  });

  const PROOF_MSG = 'Add at least one proof attachment before completing this task.';

  function isProofRequiredError(err: unknown): boolean {
    const e = err as { data?: { error?: string } } | undefined;
    return e?.data?.error === 'PROOF_REQUIRED';
  }

  const handleCreate = async () => {
    if (!workspaceId || !formData.title?.trim()) return;
    // A brand-new task can't have proof yet, so it can't be created already-done
    // while requiring proof. Surface this clearly instead of a server round-trip.
    if (!repeatEnabled && formData.proofRequired && formData.status === 'done') {
      setActionError('Create the task first, attach proof, then mark it done.');
      return;
    }
    setActionError(null);
    try {
      if (repeatEnabled) {
        await createTaskTemplate({
          workspaceId,
          title: formData.title.trim(),
          description: formData.description || undefined,
          priority: (formData.priority as TaskPriority) || 'medium',
          assigneeUserIds: formData.assigneeUserId ? [formData.assigneeUserId] : undefined,
          relatedContactId: formData.relatedContactId ?? undefined,
          goalId: formData.goalId ?? undefined,
          recurrenceRule: buildRecurrenceRule(recurrence),
          startAt: new Date(seriesStartAt).toISOString(),
          timezone,
        }).unwrap();
      } else {
        await createTask({
          workspaceId,
          title: formData.title.trim(),
          description: formData.description || undefined,
          status: (formData.status as TaskStatus) || 'todo',
          priority: (formData.priority as TaskPriority) || 'medium',
          dueDate: formData.dueDate || undefined,
          assigneeUserId: formData.assigneeUserId || undefined,
          relatedContactId: formData.relatedContactId || undefined,
          followerUserIds: followerIds.length ? followerIds : undefined,
          estimatedMinutes: formData.estimatedMinutes ?? undefined,
          blockerReason: formData.status === 'blocked' ? (formData.blockerReason ?? null) : undefined,
          goalId: formData.goalId ?? undefined,
          projectId: formData.projectId ?? undefined,
          milestoneId: formData.milestoneId ?? undefined,
          proofRequired: formData.proofRequired === true,
        }).unwrap();
      }
      onCreated?.();
      onClose();
    } catch (err) {
      if (isProofRequiredError(err)) {
        setActionError(PROOF_MSG);
      } else {
        console.error('Failed to create task:', err);
        setActionError('Failed to create task. Please try again.');
      }
    }
  };

  const handleSave = async () => {
    if (!taskId) return;
    if (proofBlocksCompletion) {
      setActionError(PROOF_MSG);
      return;
    }
    // Work Accountability: completing a task (status → done) runs through the
    // completion dialog + POST /complete, not a plain status write. To avoid
    // losing other in-progress edits, persist them first (keeping the CURRENT
    // status), then hand the completion to the parent (page-level dialog).
    if (waEnabled && formData.status === 'done' && task && task.status !== 'done') {
      setActionError(null);
      try {
        await updateTask({
          id: taskId,
          title: formData.title,
          description: formData.description || undefined,
          status: task.status, // keep current — completion handles the → done transition
          priority: formData.priority,
          dueDate: formData.dueDate || undefined,
          assigneeUserId: formData.assigneeUserId ?? null,
          relatedContactId: formData.relatedContactId || undefined,
          followerUserIds: followerIds,
          estimatedMinutes: formData.estimatedMinutes ?? null,
          blockerReason: formData.blockerReason ?? null,
          reopenReason: formData.reopenReason ?? null,
          completionScore: formData.completionScore ?? null,
          goalId: formData.goalId ?? null,
          projectId: formData.projectId ?? null,
          milestoneId: formData.milestoneId ?? null,
          proofRequired: formData.proofRequired === true,
        }).unwrap();
      } catch {
        // Non-fatal: proceed to completion even if the edit save failed.
      }
      onRequestComplete?.(task.id);
      onClose();
      return;
    }
    setActionError(null);
    try {
      await updateTask({
        id: taskId,
        title: formData.title,
        description: formData.description || undefined,
        status: formData.status,
        priority: formData.priority,
        dueDate: formData.dueDate || undefined,
        assigneeUserId: formData.assigneeUserId ?? null,
        relatedContactId: formData.relatedContactId || undefined,
        followerUserIds: followerIds,
        estimatedMinutes: formData.estimatedMinutes ?? null,
        blockerReason: formData.blockerReason ?? null,
        reopenReason: formData.reopenReason ?? null,
        completionScore: formData.completionScore ?? null,
        goalId: formData.goalId ?? null,
        projectId: formData.projectId ?? null,
        milestoneId: formData.milestoneId ?? null,
        proofRequired: formData.proofRequired === true,
      }).unwrap();
      setIsEditing(false);
    } catch (err) {
      if (isProofRequiredError(err)) {
        setActionError(PROOF_MSG);
      } else {
        console.error('Failed to update task:', err);
        setActionError('Failed to save changes. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    if (isCreate) {
      onClose();
      return;
    }
    if (task) {
      setFormData({
        title: task.title,
        description: task.description,
        status: task.status,
        priority: task.priority,
        dueDate: task.dueDate,
        assigneeUserId: task.assigneeUserId,
        relatedContactId: task.relatedContactId,
        estimatedMinutes: task.estimatedMinutes,
        blockerReason: task.blockerReason,
        reopenReason: task.reopenReason,
        completionScore: task.completionScore,
        goalId: task.goalId ?? null,
      });
      setFollowerIds((task.followers ?? []).map((f) => f.id));
    }
    setIsEditing(false);
  };

  const handleAskAI = async () => {
    if (!task) return;
    setIsCreatingConversation(true);
    try {
      const result = await createConversation({
        workspaceId,
        title: `Fix: ${task.title}`,
        mode: 'chat',
      }).unwrap();
      navigate(`/conversations/${result.session.id}?assistant=true`);
      onClose();
    } catch (err) {
      console.error('Failed to start AI conversation:', err);
    } finally {
      setIsCreatingConversation(false);
    }
  };

  const formatDateForInput = (dateStr: string | null | undefined) => {
    if (!dateStr) return '';
    return new Date(dateStr).toISOString().split('T')[0];
  };

  const modalTitle = isCreate ? 'New Task' : isEditing ? 'Edit Task' : 'Task Details';

  const footer = isCreate ? (
    <>
      <Button variant="ghost" onClick={onClose} disabled={isCreating || isCreatingTemplate}>
        Cancel
      </Button>
      <Button
        onClick={handleCreate}
        isLoading={isCreating || isCreatingTemplate}
        disabled={!formData.title?.trim()}
      >
        {repeatEnabled ? 'Create Recurring Task' : 'Create Task'}
      </Button>
    </>
  ) : isEditing ? (
    <>
      <Button variant="ghost" onClick={handleCancel} disabled={isSaving}>
        Cancel
      </Button>
      <Button onClick={handleSave} isLoading={isSaving}>
        Save Changes
      </Button>
    </>
  ) : (
    <Button onClick={() => setIsEditing(true)}>Edit Task</Button>
  );

  // ── FORM (create / edit) ────────────────────────────────────
  const renderForm = () => (
    <div className="space-y-5" key={isCreate ? 'create' : `edit-${taskId}`}>
      {/* Core fields — enough to create on their own */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
        <input
          type="text"
          autoFocus={isCreate}
          value={formData.title || ''}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="What needs to be done?"
          className={INPUT_CLS}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          value={formData.description || ''}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
          className={INPUT_CLS}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={formData.status || 'todo'}
            onChange={(e) => setFormData({ ...formData, status: e.target.value as TaskStatus })}
            className={INPUT_CLS}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_CONFIG[s].label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
          <select
            value={formData.priority || 'medium'}
            onChange={(e) => setFormData({ ...formData, priority: e.target.value as TaskPriority })}
            className={INPUT_CLS}
          >
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {PRIORITY_CONFIG[p].label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
        <input
          type="date"
          value={formatDateForInput(formData.dueDate)}
          onChange={(e) => setFormData({ ...formData, dueDate: e.target.value || undefined })}
          className={INPUT_CLS}
        />
      </div>

      {/* Advanced: People & Assignment */}
      <CollapsibleSection title="People & Assignment" defaultOpen={!isCreate}>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
          <AssigneeSelect
            value={formData.assigneeUserId}
            onChange={(userId) => setFormData({ ...formData, assigneeUserId: userId })}
            members={teamMembers}
            isLoading={isLoadingMembers}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Watchers</label>
          <WatcherPicker
            value={followerIds}
            onChange={setFollowerIds}
            members={teamMembers}
            isLoading={isLoadingMembers}
          />
        </div>
      </CollapsibleSection>

      {/* Advanced: Context */}
      <CollapsibleSection title="Context" defaultOpen={!isCreate}>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Related Contact</label>
          <select
            value={formData.relatedContactId || ''}
            onChange={(e) => setFormData({ ...formData, relatedContactId: e.target.value || undefined })}
            className={INPUT_CLS}
          >
            <option value="">None</option>
            {contacts?.map((contact) => (
              <option key={contact.id} value={contact.id}>
                {contact.fullName}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Linked Goal</label>
          <select
            value={formData.goalId ?? ''}
            onChange={(e) => setFormData({ ...formData, goalId: e.target.value || null })}
            className={INPUT_CLS}
          >
            <option value="">None</option>
            {goalsData?.goals.map((goal) => (
              <option key={goal.id} value={goal.id}>
                {goal.title}
              </option>
            ))}
          </select>
        </div>

        {/* Project → Milestone (cascading). Selecting a milestone fills its project. */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
            <select
              value={formData.projectId ?? ''}
              onChange={(e) => {
                const newProjectId = e.target.value || null;
                setFormData((prev) => {
                  const proj = projects?.find((p) => p.id === newProjectId);
                  const keepMilestone = !!proj?.milestones?.some((m) => m.id === prev.milestoneId);
                  return { ...prev, projectId: newProjectId, milestoneId: keepMilestone ? prev.milestoneId : null };
                });
              }}
              className={INPUT_CLS}
            >
              <option value="">None</option>
              {projects?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Milestone</label>
            <select
              value={formData.milestoneId ?? ''}
              disabled={!formData.projectId}
              onChange={(e) => {
                const newMilestoneId = e.target.value || null;
                setFormData((prev) => {
                  if (!newMilestoneId) return { ...prev, milestoneId: null };
                  const owning = projects?.find((p) => p.milestones?.some((m) => m.id === newMilestoneId));
                  return { ...prev, milestoneId: newMilestoneId, projectId: owning?.id ?? prev.projectId };
                });
              }}
              className={`${INPUT_CLS} disabled:bg-gray-50 disabled:text-gray-400`}
            >
              <option value="">{formData.projectId ? 'None' : 'Select a project first'}</option>
              {projects
                ?.find((p) => p.id === formData.projectId)
                ?.milestones?.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
            </select>
          </div>
        </div>
      </CollapsibleSection>

      {/* Advanced: Planning */}
      <CollapsibleSection title="Planning" defaultOpen={!isCreate}>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Estimate
            <span className="ml-1 text-xs font-normal text-gray-400">(minutes)</span>
          </label>
          <input
            type="number"
            min={1}
            value={formData.estimatedMinutes ?? ''}
            onChange={(e) => {
              const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
              setFormData({ ...formData, estimatedMinutes: val });
            }}
            placeholder="e.g. 90"
            className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {formData.status === 'blocked' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Blocker Reason</label>
            <textarea
              value={formData.blockerReason || ''}
              onChange={(e) => setFormData({ ...formData, blockerReason: e.target.value || null })}
              rows={2}
              placeholder="Optional: describe why this task is blocked"
              className={INPUT_CLS}
            />
          </div>
        )}

        {!isCreate && task?.status === 'done' && formData.status !== 'done' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reopen Reason</label>
            <textarea
              value={formData.reopenReason || ''}
              onChange={(e) => setFormData({ ...formData, reopenReason: e.target.value || null })}
              rows={2}
              placeholder="Optional: describe why this task was reopened"
              className={INPUT_CLS}
            />
          </div>
        )}

        {!isCreate && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Completion Score
              <span className="ml-1 text-xs font-normal text-gray-400">(0–100, optional)</span>
            </label>
            <input
              type="number"
              min={0}
              max={100}
              value={formData.completionScore ?? ''}
              onChange={(e) => {
                const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
                setFormData({ ...formData, completionScore: val });
              }}
              placeholder="e.g. 85"
              className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        )}
      </CollapsibleSection>

      {/* Advanced: Attachments & Proof */}
      <CollapsibleSection title="Attachments & Proof" defaultOpen={!isCreate && !!formData.proofRequired}>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            checked={!!formData.proofRequired}
            onChange={(e) => setFormData({ ...formData, proofRequired: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Require proof before this task can be completed
        </label>

        {isCreate || !task ? (
          <p className="text-xs text-gray-500">Save the task first to add attachments or proof files.</p>
        ) : (
          <TaskAttachments
            taskId={task.id}
            workspaceId={workspaceId}
            proofRequired={!!formData.proofRequired}
            onProofCountChange={setProofCount}
          />
        )}
      </CollapsibleSection>

      {/* Advanced: Repeat — create only. Turning this on creates a recurring series. */}
      {isCreate && (
        <CollapsibleSection title="Repeat">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              checked={repeatEnabled}
              onChange={(e) => setRepeatEnabled(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Repeat this task on a schedule
          </label>

          {repeatEnabled && (
            <div className="space-y-4 pt-1">
              <RecurrenceFields value={recurrence} onChange={setRecurrence} />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Starts</label>
                <input
                  type="datetime-local"
                  value={seriesStartAt}
                  onChange={(e) => setSeriesStartAt(e.target.value)}
                  className={INPUT_CLS}
                />
              </div>
              <p className="text-xs text-gray-500">
                Creates a recurring series — the first task appears at the next occurrence on or after
                the start. Recurring tasks carry title, description, priority, assignee, related
                contact, linked goal and schedule onto every generated task; watchers, estimate and
                due date apply to one-off tasks only. Manage the series later under{' '}
                <Link to="/recurring-tasks" className="text-blue-600 hover:underline" onClick={onClose}>
                  Recurring Tasks
                </Link>
                .
              </p>
            </div>
          )}
        </CollapsibleSection>
      )}

      {/* Checklist is available once the task exists. */}
      {!isCreate && task && (
        <div className="pt-2 border-t border-gray-200">
          <TaskChecklist taskId={task.id} />
        </div>
      )}

      {/* Completion gating + action feedback */}
      {proofBlocksCompletion && !actionError && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <span className="text-amber-600 text-base leading-none mt-0.5">⚠</span>
          <p className="text-sm text-amber-800">{PROOF_MSG}</p>
        </div>
      )}
      {actionError && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <span className="text-red-600 text-base leading-none mt-0.5">⚠</span>
          <p className="text-sm text-red-700">{actionError}</p>
        </div>
      )}
    </div>
  );

  // ── VIEW (read-only existing task) ──────────────────────────
  const renderView = (t: Task) => {
    const isOverdue =
      t.status !== 'done' &&
      !!t.dueDate &&
      t.dueDate.split('T')[0] < new Date().toISOString().split('T')[0];
    const assigneeCapacity =
      t.assigneeUserId && managementSummary
        ? managementSummary.capacity.find((c) => c.userId === t.assigneeUserId)
        : null;
    const assigneeName = t.assigneeUserId
      ? (() => {
          const m = teamMembers?.find((tm) => tm.userId === t.assigneeUserId);
          return m ? m.fullName?.trim() || m.email : 'Assigned';
        })()
      : 'Unassigned';

    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
          <p className="text-lg font-semibold text-gray-900">{t.title}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <p className="text-gray-900 whitespace-pre-wrap">{t.description || 'No description'}</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <span
              className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${STATUS_CONFIG[t.status].bgColor} ${STATUS_CONFIG[t.status].color}`}
            >
              {STATUS_CONFIG[t.status].label}
            </span>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <span className={`text-sm font-medium ${PRIORITY_CONFIG[t.priority].color}`}>
              {PRIORITY_CONFIG[t.priority].label}
            </span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
          <p className="text-gray-900">
            {t.dueDate ? new Date(t.dueDate).toLocaleDateString() : 'No due date'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
          <p className="text-gray-900">{assigneeName}</p>
        </div>

        {t.status === 'blocked' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Blocker Reason</label>
            <p className="text-gray-900 whitespace-pre-wrap">{t.blockerReason || 'No reason provided'}</p>
          </div>
        )}

        {!!t.reopenReason && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reopen Reason</label>
            <p className="text-gray-900 whitespace-pre-wrap">{t.reopenReason}</p>
          </div>
        )}

        {t.completionScore != null && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Completion Score</label>
            <p className="text-gray-900">{t.completionScore}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Related Contact</label>
          <p className="text-gray-900">{t.relatedContact?.fullName || 'No related contact'}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Watchers</label>
          {t.followers && t.followers.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {t.followers.map((f) => (
                <span key={f.id} className="text-sm text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full">
                  {f.fullName?.trim() || f.email}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-gray-900">No watchers</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Linked Goal</label>
          <p className="text-gray-900">
            {t.goalId
              ? goalsData?.goals.find((g) => g.id === t.goalId)?.title ?? 'Linked goal'
              : 'No linked goal'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Project / Milestone</label>
          <p className="text-gray-900">
            {(() => {
              const proj = projects?.find((p) => p.id === t.projectId);
              const ms = proj?.milestones?.find((m) => m.id === t.milestoneId);
              if (!t.projectId) return 'No project';
              return `${proj?.name ?? 'Project'}${ms ? ` › ${ms.name}` : ''}`;
            })()}
          </p>
        </div>

        {/* PatchProof external source indicator */}
        {t.source === 'patchproof' && (
          <div className="py-3 px-4 bg-orange-50 rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-orange-700">PatchProof</span>
            </div>
            <div className="text-sm text-orange-600 space-y-1">
              {!!t.externalMeta?.severity && (
                <div className="flex items-center gap-2">
                  <span className="text-orange-500">Severity:</span>
                  <span className="capitalize">{String(t.externalMeta.severity)}</span>
                </div>
              )}
              {!!t.externalMeta?.repositoryName && (
                <div className="flex items-center gap-2">
                  <span className="text-orange-500">Repo:</span>
                  <span>{String(t.externalMeta.repositoryName)}</span>
                </div>
              )}
              {!!t.externalMeta?.environmentName && (
                <div className="flex items-center gap-2">
                  <span className="text-orange-500">Env:</span>
                  <span>{String(t.externalMeta.environmentName)}</span>
                </div>
              )}
              {!!t.externalMeta?.cveId && (
                <div className="flex items-center gap-2">
                  <span className="text-orange-500">CVE:</span>
                  <span>{String(t.externalMeta.cveId)}</span>
                </div>
              )}
              {!!t.externalMeta?.patchproofUrl && (
                <a
                  href={String(t.externalMeta.patchproofUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-800 hover:underline mt-1"
                >
                  View in PatchProof
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Recurring task indicator (read-only) */}
        {t.taskTemplateId && (
          <div className="py-3 px-4 bg-blue-50 rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-700">Recurring Task</span>
            </div>
            {template && (
              <div className="text-sm text-blue-600 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-blue-500">Template:</span>
                  <span>{template.title}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-3.5 w-3.5 text-blue-400" />
                  <span>{formatRecurrence(template.recurrenceRule)}</span>
                </div>
                {template.nextOccurrenceAt && (
                  <div className="flex items-center gap-2">
                    <span className="text-blue-500">Next:</span>
                    <span>{new Date(template.nextOccurrenceAt).toLocaleDateString()}</span>
                  </div>
                )}
                <Link
                  to="/recurring-tasks"
                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline mt-1"
                  onClick={onClose}
                >
                  View rule
                  <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Work Accountability (PR-E) — flag-gated. */}
        {waEnabled && (
          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Work Accountability</h3>
            <TaskAccountabilitySection
              task={{ id: t.id, workspaceId, assigneeUserId: t.assigneeUserId ?? null, estimatedMinutes: t.estimatedMinutes ?? null, projectId: t.projectId ?? null }}
            />
          </div>
        )}

        {/* Management Signals */}
        <div className="pt-4 border-t border-gray-200 space-y-3">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Management Signals
          </h3>

          {isOverdue && !t.blockerReason && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg">
              <span className="text-red-500 text-base mt-0.5">⚠</span>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-700">Overdue — no blocker context recorded</p>
                <button onClick={() => setIsEditing(true)} className="text-xs text-red-600 hover:underline mt-0.5">
                  Add context →
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Estimate</span>
            {t.estimatedMinutes ? (
              <span className="font-medium text-gray-900">{formatMinutes(t.estimatedMinutes)}</span>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="text-xs text-gray-400 italic hover:text-gray-600"
              >
                Not estimated — add estimate
              </button>
            )}
          </div>

          {t.completedAt && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Completed</span>
              <span className="font-medium text-gray-900">
                {new Date(t.completedAt).toLocaleDateString()}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Assignee load</span>
            {t.assigneeUserId ? (
              assigneeCapacity ? (
                <span className="font-medium text-gray-900 text-right">
                  {assigneeCapacity.openTaskCount} open
                  {assigneeCapacity.estimatedMinutes > 0
                    ? `, ~${Math.round(assigneeCapacity.estimatedMinutes / 60)}h est.`
                    : assigneeCapacity.unestimatedTaskCount > 0
                    ? ` (+${assigneeCapacity.unestimatedTaskCount} unestimated)`
                    : ''}
                </span>
              ) : (
                <span className="text-xs text-gray-400 italic">No capacity data</span>
              )
            ) : (
              <span className="text-xs text-gray-400 italic">Unassigned</span>
            )}
          </div>

          <button
            onClick={handleAskAI}
            disabled={isCreatingConversation}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-50 hover:bg-purple-100 border border-purple-200 text-purple-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {isCreatingConversation ? 'Opening assistant...' : 'Ask AI how to fix this'}
          </button>

          {/* Smart Assignment */}
          <div className="pt-3 border-t border-gray-100">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Smart Assignment
            </h3>
            {!reassignment || reassignment.suggestions.length === 0 ? (
              <p className="text-xs text-gray-400 italic">
                {reassignment ? 'No better assignment found' : 'Loading suggestions…'}
              </p>
            ) : (
              <div className="space-y-1.5">
                {reassignment.currentAssigneeUserId == null && (
                  <p className="text-xs text-gray-400 mb-1">Best available owners:</p>
                )}
                {reassignment.suggestions.map((s, idx) => (
                  <div
                    key={s.userId}
                    className={`flex items-center gap-2 p-2 rounded-lg border text-sm ${
                      idx === 0 ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-100'
                    }`}
                  >
                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600 flex-shrink-0">
                      {s.userId.substring(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs truncate ${idx === 0 ? 'text-blue-800 font-medium' : 'text-gray-700'}`}>
                        {s.reason}
                      </p>
                    </div>
                    <button
                      onClick={() => updateTask({ id: t.id, assigneeUserId: s.userId })}
                      className={`flex-shrink-0 px-2 py-1 rounded text-xs font-medium transition-colors ${
                        idx === 0
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      Reassign
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Checklist */}
        <div className="pt-2 border-t border-gray-200">
          <TaskChecklist taskId={t.id} />
        </div>

        {/* Metadata */}
        <div className="pt-4 border-t border-gray-200 text-xs text-gray-500">
          <p>Created: {new Date(t.createdAt).toLocaleDateString()}</p>
          <p>Updated: {new Date(t.updatedAt).toLocaleDateString()}</p>
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal isOpen={open} onClose={onClose} title={modalTitle} size="lg" footer={footer}>
        {isCreate ? (
          renderForm()
        ) : isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500">Failed to load task details.</p>
          </div>
        ) : task ? (
          showForm ? renderForm() : renderView(task)
        ) : null}
      </Modal>
    </>
  );
}
